/**
 * FI Console Dashboard
 *
 * Main dashboard view for Financial Institutions to monitor business compliance.
 * Uses a grid layout with collapsible role menu matching the Superintendent page style.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  Text,
  Badge,
  Group,
  Stack,
  Title,
  Progress,
  Loader,
  Alert,
  SimpleGrid,
  ThemeIcon,
  Tooltip,
  Select,
} from '@mantine/core';
import {
  IconChartPie,
  IconAlertTriangle,
  IconChecklist,
  IconRobot,
} from '@tabler/icons-react';
import { fiConsoleApi } from '../services/fiConsoleApi';
import GapDrawer from '../components/GapDrawer';
import ActionItemsTimeline from '../components/ActionItemsTimeline';
import TopNav from '../components/TopNav';
import type {
  DashboardResponse,
  GuidelineComplianceSummary,
  ComplianceGap,
  Severity,
} from '../types/fiConsole';

/**
 * Get color based on compliance score
 * >= 85%: green (compliant)
 * 70-84%: yellow (partial)
 * 50-69%: orange (concerning)
 * < 50%: red (critical)
 */
function getComplianceColor(score: number): string {
  if (score >= 85) return 'green';
  if (score >= 70) return 'yellow';
  if (score >= 50) return 'orange';
  return 'red';
}

/**
 * Get color for severity badge
 */
function getSeverityColor(severity: Severity): string {
  const severityColors: Record<Severity, string> = {
    CRITICAL: 'red',
    HIGH: 'orange',
    MEDIUM: 'yellow',
    LOW: 'blue',
  };
  return severityColors[severity] || 'gray';
}

/**
 * Format date string to readable format
 */
function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return 'Invalid date';
  }
}

/**
 * Stats card component for executive summary
 */
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: string;
}

function StatCard({ title, value, subtitle, icon, color = 'blue' }: StatCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" mb="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
          {title}
        </Text>
        {icon && (
          <ThemeIcon color={color} variant="light" size="lg" radius="md">
            {icon}
          </ThemeIcon>
        )}
      </Group>
      <Text size="xl" fw={700} mb={4}>
        {value}
      </Text>
      {subtitle && (
        <Text size="xs" c="dimmed">
          {subtitle}
        </Text>
      )}
    </Card>
  );
}

/**
 * Guideline tile component
 */
interface GuidelineTileProps {
  guideline: GuidelineComplianceSummary;
  onClick?: () => void;
}

function GuidelineTile({ guideline, onClick }: GuidelineTileProps) {
  const color = getComplianceColor(guideline.compliance_score);
  const hasGaps = guideline.critical_gaps_count > 0;

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      style={{ cursor: onClick ? 'pointer' : 'default', height: '100%' }}
      onClick={onClick}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Text size="sm" fw={600} mb={4}>
              {guideline.guideline_id}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2}>
              {guideline.guideline_name}
            </Text>
          </div>
          {hasGaps && (
            <Tooltip label={`${guideline.critical_gaps_count} critical gap(s)`}>
              <Badge color="red" size="sm" variant="filled">
                !
              </Badge>
            </Tooltip>
          )}
        </Group>

        <div>
          <Group justify="space-between" mb={4}>
            <Text size="xs" fw={500}>
              Compliance
            </Text>
            <Text size="xs" fw={700} c={color}>
              {guideline.compliance_score.toFixed(1)}%
            </Text>
          </Group>
          <Progress value={guideline.compliance_score} color={color} size="sm" radius="xl" />
        </div>

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {guideline.compliant_controls}/{guideline.total_controls} controls
          </Text>
          {guideline.gaps_count > 0 && (
            <Badge color={color} size="sm" variant="light">
              {guideline.gaps_count} gap{guideline.gaps_count !== 1 ? 's' : ''}
            </Badge>
          )}
        </Group>

        {guideline.last_assessment_date && (
          <Text size="xs" c="dimmed">
            Last assessed: {formatDate(guideline.last_assessment_date)}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Gap card component
 */
interface GapCardProps {
  gap: ComplianceGap;
  onClick?: () => void;
}

function GapCard({ gap, onClick }: GapCardProps) {
  return (
    <Card withBorder padding="sm" radius="md" style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Group justify="space-between" mb="xs">
        <Badge color={getSeverityColor(gap.severity)} size="sm">
          {gap.severity}
        </Badge>
        <Text size="xs" c="dimmed">
          {formatDate(gap.created_at)}
        </Text>
      </Group>
      <Text size="sm" fw={600} mb={4}>
        {gap.title}
      </Text>
      <Text size="xs" c="dimmed" lineClamp={2} mb="xs">
        {gap.description}
      </Text>
      <Group justify="space-between">
        <Badge size="xs" variant="light">
          {gap.guideline_id}
        </Badge>
        <Badge size="xs" color={gap.status === 'OPEN' ? 'red' : 'blue'}>
          {gap.status}
        </Badge>
      </Group>
    </Card>
  );
}


/**
 * Main FI Console Dashboard component with grid layout
 */
export default function FIConsoleDashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInstitution, setSelectedInstitution] = useState('rbc');

  // Company options
  const institutionOptions = [
    { value: 'rbc', label: 'Royal Bank of Canada' },
    { value: 'td', label: 'TD Bank' },
    { value: 'bmo', label: 'Bank of Montreal' },
    { value: 'scotia', label: 'Scotiabank' },
    { value: 'cibc', label: 'CIBC' },
    { value: 'national', label: 'National Bank of Canada' },
    { value: 'desjardins', label: 'Desjardins Group' },
    { value: 'manulife', label: 'Manulife Financial' },
    { value: 'sunlife', label: 'Sun Life Financial' },
    { value: 'intact', label: 'Intact Financial' },
  ];

  // Gap drawer state
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedGapId, setSelectedGapId] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    try {
      setLoading(true);
      setError(null);
      const data = await fiConsoleApi.getDashboard();
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }

  const handleGapClick = (gapId: string) => {
    setSelectedGapId(gapId);
    setDrawerOpened(true);
  };

  const handleGapUpdate = (updatedGap: ComplianceGap) => {
    // Optimistic UI update
    if (dashboardData) {
      setDashboardData({
        ...dashboardData,
        recent_gaps: dashboardData.recent_gaps.map((gap) =>
          gap.id === updatedGap.id ? updatedGap : gap
        ),
      });
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#f8f9fa'
    }}>
      {/* Top Navigation */}
      <TopNav />

      {/* Sub-header with institution selector */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        alignItems: 'center',
        padding: '8px 24px',
        justifyContent: 'flex-end',
      }}>
        <Select
          data={institutionOptions}
          value={selectedInstitution}
          onChange={(value) => setSelectedInstitution(value || 'rbc')}
          size="sm"
          w={220}
          styles={{
            input: {
              fontWeight: 500,
            }
          }}
        />
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: 24
      }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text c="dimmed">Loading compliance dashboard...</Text>
            </Stack>
          </div>
        )}

        {error && (
          <Alert color="red" title="Error Loading Dashboard" style={{ maxWidth: 600, margin: '0 auto' }}>
            {error}
          </Alert>
        )}

        {!loading && !error && !dashboardData && (
          <Alert color="yellow" title="No Data Available">
            No dashboard data available. Please check your connection to the backend.
          </Alert>
        )}

        {!loading && !error && dashboardData && (
          <Stack gap="lg">
            {/* Header */}
            <div>
              <Title order={2} mb="xs">
                {institutionOptions.find(i => i.value === selectedInstitution)?.label || 'Company'} - Performance Dashboard
              </Title>
              <Text c="dimmed" size="sm">
                Last updated: {formatDate(dashboardData.last_updated)}
              </Text>
            </div>

            {/* Executive Summary Statistics */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
              <StatCard
                title="Overall Compliance"
                value={`${dashboardData.summary.overall_compliance_score.toFixed(1)}%`}
                subtitle={`${dashboardData.summary.guidelines_above_threshold} of ${dashboardData.summary.total_guidelines} guidelines above 85%`}
                icon={<IconChartPie size={20} />}
                color={getComplianceColor(dashboardData.summary.overall_compliance_score)}
              />
              <StatCard
                title="Critical Gaps"
                value={dashboardData.summary.critical_gaps_count}
                subtitle={`${dashboardData.summary.high_gaps_count} high priority gaps`}
                icon={<IconAlertTriangle size={20} />}
                color={dashboardData.summary.critical_gaps_count > 0 ? 'red' : 'green'}
              />
              <StatCard
                title="Total Guidelines"
                value={dashboardData.summary.total_guidelines}
                subtitle={`${dashboardData.summary.total_controls} total controls`}
                icon={<IconChecklist size={20} />}
                color="blue"
              />
              <StatCard
                title="Automation"
                value={`${dashboardData.summary.automated_controls_pct.toFixed(0)}%`}
                subtitle={`${dashboardData.summary.compliant_controls} compliant controls`}
                icon={<IconRobot size={20} />}
                color="cyan"
              />
            </SimpleGrid>

            {/* Guideline Compliance Matrix */}
            <div>
              <Title order={3} mb="md">
                Guideline Compliance
              </Title>
              <Grid gutter="md">
                {dashboardData.guidelines.map((guideline) => (
                  <Grid.Col key={guideline.guideline_id} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
                    <GuidelineTile
                      guideline={guideline}
                      onClick={() => navigate(`/fi-console/guideline/${guideline.guideline_id}`)}
                    />
                  </Grid.Col>
                ))}
              </Grid>
            </div>

            {/* Recent Gaps and Action Items */}
            <Grid gutter="lg">
              {/* Recent Gaps */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Title order={3} mb="md">
                  Recent Gaps
                </Title>
                {dashboardData.recent_gaps.length === 0 ? (
                  <Card withBorder padding="lg">
                    <Text c="dimmed" ta="center">
                      No gaps detected
                    </Text>
                  </Card>
                ) : (
                  <Stack gap="sm">
                    {dashboardData.recent_gaps.slice(0, 5).map((gap) => (
                      <GapCard key={gap.id} gap={gap} onClick={() => handleGapClick(gap.id)} />
                    ))}
                  </Stack>
                )}
              </Grid.Col>

              {/* Action Items */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Title order={3} mb="md">
                  Action Items
                </Title>
                <ActionItemsTimeline
                  actionItems={dashboardData.action_items}
                  onGapClick={handleGapClick}
                  maxItems={5}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        )}
      </div>

      {/* Gap Drawer */}
      <GapDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        gapId={selectedGapId}
        onUpdate={handleGapUpdate}
      />
    </div>
  );
}
